import { useEffect, useState } from 'react';
import { useLanguage } from '../../hooks/useLanguage';
import NavbarNew from '../home/components/NavbarNew';
import FooterNew from '../home/components/FooterNew';
import ContactFormNew from '../home/components/ContactFormNew';

export default function ContactPage() {
  const [scrolled, setScrolled] = useState(false);
  const { language } = useLanguage();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const siteUrl = import.meta.env.VITE_SITE_URL || 'https://teliq.one';
    const ogImageUrl = 'https://static.readdy.ai/image/517d8e45247682e1e610f69e9004e6d7/27d85eeddc9e07e72840b82b740df7b1.svg';

    const content = {
      de: {
        title: 'Kontakt - TELIQone KI-Prozessautomatisierung',
        description: 'Kontaktieren Sie TELIQone für eine kostenlose Demo unserer KI-gestützten Prozessautomatisierung für Telekommunikationsunternehmen. Persönliche Beratung innerhalb von 24 Stunden.',
        locale: 'de_DE'
      },
      en: {
        title: 'Contact - TELIQone AI Process Automation',
        description: 'Contact TELIQone for a free demo of our AI-powered process automation for telecommunications companies. Personal consultation within 24 hours.',
        locale: 'en_US'
      }
    };

    const t = content[language as keyof typeof content] ?? content.de;

    // Page title
    document.title = t.title;

    // Meta description
    let metaDesc = document.querySelector('meta[name="description"]');
    if (!metaDesc) {
      metaDesc = document.createElement('meta');
      metaDesc.setAttribute('name', 'description');
      document.head.appendChild(metaDesc);
    }
    metaDesc.setAttribute('content', t.description);

    // Robots
    let metaRobots = document.querySelector('meta[name="robots"]');
    if (!metaRobots) {
      metaRobots = document.createElement('meta');
      metaRobots.setAttribute('name', 'robots');
      document.head.appendChild(metaRobots);
    }
    metaRobots.setAttribute('content', 'index, follow');

    // Canonical
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', `${siteUrl}/contact`);

    // Open Graph tags
    const ogTags = [
      { property: 'og:title', content: t.title },
      { property: 'og:description', content: t.description },
      { property: 'og:url', content: `${siteUrl}/contact` },
      { property: 'og:image', content: ogImageUrl },
      { property: 'og:type', content: 'website' },
      { property: 'og:site_name', content: 'TELIQone' },
      { property: 'og:locale', content: t.locale }
    ];

    ogTags.forEach(({ property, content }) => {
      let tag = document.querySelector(`meta[property="${property}"]`);
      if (!tag) {
        tag = document.createElement('meta');
        tag.setAttribute('property', property);
        document.head.appendChild(tag);
      }
      tag.setAttribute('content', content);
    });

    // Twitter Card tags
    const twitterTags = [
      { name: 'twitter:card', content: 'summary_large_image' },
      { name: 'twitter:title', content: t.title },
      { name: 'twitter:description', content: t.description },
      { name: 'twitter:image', content: ogImageUrl }
    ];

    twitterTags.forEach(({ name, content }) => {
      let tag = document.querySelector(`meta[name="${name}"]`);
      if (!tag) {
        tag = document.createElement('meta');
        tag.setAttribute('name', name);
        document.head.appendChild(tag);
      }
      tag.setAttribute('content', content);
    });

    // Schema.org JSON-LD
    const existingSchema = document.getElementById('contact-page-schema');
    if (existingSchema) existingSchema.remove();

    const schemaScript = document.createElement('script');
    schemaScript.type = 'application/ld+json';
    schemaScript.id = 'contact-page-schema';
    schemaScript.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'ContactPage',
      '@id': `${siteUrl}/contact#webpage`,
      name: language === 'de' ? 'TELIQone Kontakt' : 'TELIQone Contact',
      url: `${siteUrl}/contact`,
      description: t.description,
      inLanguage: ['de', 'en'],
      dateModified: new Date().toISOString(),
      isPartOf: {
        '@type': 'WebSite',
        '@id': `${siteUrl}/#website`,
        name: 'TELIQone',
        url: siteUrl
      },
      breadcrumb: {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: siteUrl },
          { '@type': 'ListItem', position: 2, name: language === 'de' ? 'Kontakt' : 'Contact', item: `${siteUrl}/contact` }
        ]
      },
      mainEntity: {
        '@type': 'Organization',
        '@id': `${siteUrl}/#organization`,
        name: 'omniIT GmbH',
        url: siteUrl,
        logo: ogImageUrl,
        contactPoint: {
          '@type': 'ContactPoint',
          telephone: '+49-89-998-2419-0',
          contactType: 'customer service',
          areaServed: ['DE', 'AT', 'CH'],
          availableLanguage: ['German', 'English'],
          email: 'info@omniit.de'
        },
        address: {
          '@type': 'PostalAddress',
          streetAddress: 'Grünwalder Weg 28b',
          addressLocality: 'Oberhaching',
          postalCode: '82041',
          addressCountry: 'DE'
        }
      }
    });
    document.head.appendChild(schemaScript);

    // Cleanup function
    return () => {
      const s = document.getElementById('contact-page-schema');
      if (s) s.remove();

      // Clean up OG tags
      ['og:title', 'og:description', 'og:url', 'og:image', 'og:type', 'og:site_name', 'og:locale'].forEach(prop => {
        const tag = document.querySelector(`meta[property="${prop}"]`);
        if (tag) tag.remove();
      });

      // Clean up Twitter tags
      ['twitter:card', 'twitter:title', 'twitter:description', 'twitter:image'].forEach(name => {
        const tag = document.querySelector(`meta[name="${name}"]`);
        if (tag) tag.remove();
      });
    };
  }, [language]);

  const content = {
    de: {
      badge: 'Kontakt',
      headline: 'Lassen Sie uns sprechen',
      subtext: 'Wir freuen uns darauf, von Ihnen zu hören und Ihnen zu zeigen, wie TELIQone Ihre Telco-Prozesse transformieren kann.'
    },
    en: {
      badge: 'Contact',
      headline: "Let's Talk",
      subtext: 'We look forward to hearing from you and showing you how TELIQone can transform your telco processes.'
    }
  };

  const t = content[language as keyof typeof content] ?? content.de;

  return (
    <div className="min-h-screen bg-white">
      <NavbarNew scrolled={scrolled} />

      {/* Hero Section */}
      <section className="pt-32 pb-12 bg-gradient-to-b from-black to-black/95">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 text-center">
          <span className="text-xs font-bold tracking-widest uppercase text-emerald-400 mb-4 block">
            {t.badge}
          </span>
          <h1 className="text-4xl sm:text-5xl font-black text-white mb-6 leading-tight">
            {t.headline}
          </h1>
          <p className="text-white/60 text-lg max-w-2xl mx-auto leading-relaxed">
            {t.subtext}
          </p>
        </div>
      </section>

      {/* Contact Form Section */}
      <ContactFormNew />

      <FooterNew />
    </div>
  );
}